/// Compressão de foto **no aparelho**, antes de guardar.
///
/// Uma foto de celular moderno tem 3 a 8 MB. Guardar isso no IndexedDB enche a cota
/// do navegador em poucas dezenas de fotos, e subir pelo 4G de uma obra é lento e
/// caro. O relatório precisa de 1600px; o resto é peso morto.
///
/// O servidor comprime de novo ao receber — é a rede de segurança para foto que
/// chegue por outro caminho.

const LADO_MAXIMO = 1600
const QUALIDADE = 0.82

export async function comprimirImagem(arquivo: File | Blob): Promise<Blob> {
  const bitmap = await criarBitmap(arquivo)
  try {
    const { largura, altura } = caber(bitmap.width, bitmap.height)

    // Já é pequena o bastante e o formato serve: não recomprime — recomprimir JPEG
    // sempre perde qualidade sem ganhar espaço relevante.
    if (largura === bitmap.width && altura === bitmap.height && arquivo.type === 'image/jpeg') {
      return arquivo instanceof Blob ? arquivo : new Blob([arquivo])
    }

    const canvas = document.createElement('canvas')
    canvas.width = largura
    canvas.height = altura
    const ctx = canvas.getContext('2d')
    if (!ctx) return arquivo as Blob

    ctx.drawImage(bitmap, 0, 0, largura, altura)
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', QUALIDADE))
    return blob ?? (arquivo as Blob)
  } finally {
    bitmap.close?.()
  }
}

/// `createImageBitmap` com `imageOrientation: 'from-image'` respeita o EXIF. Sem
/// isso, foto tirada com o celular na vertical aparece deitada no relatório.
async function criarBitmap(arquivo: File | Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(arquivo, { imageOrientation: 'from-image' })
  } catch {
    return createImageBitmap(arquivo)
  }
}

function caber(largura: number, altura: number) {
  const maior = Math.max(largura, altura)
  if (maior <= LADO_MAXIMO) return { largura, altura }
  const fator = LADO_MAXIMO / maior
  return { largura: Math.round(largura * fator), altura: Math.round(altura * fator) }
}

export function formatarBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`
  return `${(n / 1024 ** 3).toFixed(1)} GB`
}
